import { InputIcon } from '@platform/ui-library';
import { FC, FormEvent } from 'react';
import {
  AutocompleteControlProps,
  ControlWithFormField,
  FormFieldControlProps,
  RadioGroupControl,
  SwitchControl
} from '@app/form_components';
import {
  FormControlProps,
  FormControlWithOptionProps,
  FormInputProps,
  ValueOrVariable,
  ValueOrVariableType,
  ValueTypeForVariableType,
  VariableConfigField,
  VariableReference,
  VariableReferenceScope,
  VariableType
} from '@app/types';
import { safeParseInt, VariableOption } from '@app/utilities';
import { VariableAutocompleteControl } from './VariableAutocomplete';

type ValueType = boolean | number | string | null;

const valueVariableDefaults = new Map<VariableType, ValueType>([
  [VariableType.boolean, false],
  [VariableType.number, null],
  [VariableType.string, '']
]);

export type ValueOrVariableAutocompleteControlProps<T extends VariableType = VariableType> = (
  FormControlWithOptionProps<
    ValueOrVariable<ValueTypeForVariableType<T>>,
    VariableOption | null
  >
  & FormInputProps
  & Pick<AutocompleteControlProps<VariableReference, VariableOption>, 'labels'>
  & {
    /** Scope(s) to include in the variable suggestions. */
    searchScopes?: VariableReferenceScope[];
    /**
     * Type(s) to include in the variable suggestions.
     *
     * @note Defaults to same type as @see valueType when set and all types when not.
     */
    searchTypes?: VariableType[];
    /** Allow user to enter values instead of a variable. */
    valueType?: T;
  }
);

function ValueOrVariableAutocompleteControl<T extends VariableType>({
  labels,
  searchScopes,
  searchTypes,
  valueType,
  ...inputProps
}: ValueOrVariableAutocompleteControlProps<T>): ReturnType<FC> {
  const {
    name,
    onChange,
    value
  } = inputProps;
  searchTypes ??= (valueType) ? [valueType] : undefined;
  const inValueMode = value?.type === ValueOrVariableType.value;

  return (
    <>
      {(!valueType || !valueVariableDefaults.has(valueType)) ? null : (
        <RadioGroupControl
          name={`${name}-val-or-var`}
          options={[
            { label: 'Value', value: true },
            { label: 'Variable', value: false }
          ]}
          onChange={(newInValueMode) => {
            onChange(
              (newInValueMode) ? {
                type: ValueOrVariableType.value,
                value: valueVariableDefaults.get(valueType) as ValueTypeForVariableType<T>
              } : {
                type: ValueOrVariableType.variable,
                varName: ''
              },
              null
            );
          }}
          value={inValueMode}
        />
      )}
      {(inValueMode) ? (
        <ValueInput
          {...inputProps}
          onChange={(newValue) => {
            onChange(
              {
                type: ValueOrVariableType.value,
                value: newValue as ValueTypeForVariableType<T>
              },
              null
            );
          }}
          valueType={valueType}
          value={value.value}
        />
      ) : (
        <VariableAutocompleteControl
          {...inputProps}
          labels={{
            isLoading: 'Searching variables...',
            notFound: '',
            ...labels
          }}
          searchScopes={searchScopes}
          searchTypes={searchTypes}
          value={value as VariableConfigField}
        />
      )}
    </>
  );
};

export type ValueOrVariableAutocompleteProps<T extends VariableType> = (
  ValueOrVariableAutocompleteControlProps<T> & FormFieldControlProps
);

export function ValueOrVariableAutocomplete<T extends VariableType>(
  props: ValueOrVariableAutocompleteProps<T>
): ReturnType<FC> {
  return (
    <ControlWithFormField control={ValueOrVariableAutocompleteControl} {...props} />
  );
};

type ValueInputProps = (
  FormControlProps<boolean | number | string | null>
  & Partial<FormInputProps>
  & Pick<ValueOrVariableAutocompleteControlProps, 'valueType'>
);

const ValueInput: FC<ValueInputProps> = ({
  onChange,
  placeholder,
  valueType,
  value,
  ...props
}) => {
  switch (valueType) {
    case VariableType.boolean:
      return (
        <SwitchControl
          {...props}
          onChange={onChange}
          value={value as boolean ?? false}
        />
      );
    case VariableType.number:
      return (
        <InputIcon
          {...props}
          onChange={(e) => {
            const validValue = e.currentTarget.value.replace(/[^0-9]/g, '');
            onChange(safeParseInt(validValue));
          }}
          placeholder={placeholder}
          type="text"
          value={(value == null) ? '' : `${value}`}
        />
      );
    case VariableType.string:
      return (
        <InputIcon
          {...props}
          onChange={(event: FormEvent<HTMLInputElement>) => {
            onChange(event.currentTarget.value);
          }}
          placeholder={placeholder}
          type="text"
          value={value as string ?? ''}
        />
      );
  }

  return (
    <p>Unsupported value type {valueType}. {window.l.notification.ask_for_help}</p>
  );
};
